'use client';

import type { ImageLoaderProps, ImageProps as NextImageProps } from 'next/image';
import NextImage from 'next/image';

import { memo } from 'react';

import Imgproxy from 'imgproxy';
import type { ResizingType } from 'imgproxy/dist/types';
import { GravityType } from 'imgproxy/dist/types';

export interface ImageProps extends NextImageProps {
  resizeType?: ResizingType;
  format?: 'webp' | 'png' | 'jpg' | 'svg';
}

const aspectRatioHeight = (originalWidth: number, originalHeight: number, width: number): number =>
  Math.round((originalHeight / originalWidth) * width);

const configureLoader = ({
  width: originalWidth,
  height: originalHeight,
  resizeType = 'fill',
  format = 'webp',
}: Pick<ImageProps, 'width' | 'height' | 'resizeType' | 'format' | 'src'>) => {
  return ({ src: url, width: loaderWidth, quality = 60 }: ImageLoaderProps) => {
    const loaderHeight =
      originalWidth && originalHeight
        ? aspectRatioHeight(Number(originalWidth), Number(originalHeight), loaderWidth)
        : undefined;

    return generateUrl({
      url,
      width: loaderWidth,
      height: loaderHeight,
      resizeType,
      quality,
      format,
    });
  };
};

const imageProxyApi = new Imgproxy({
  baseUrl: process.env['NEXT_PUBLIC_IMGPROXY_URL'] || '',
  key: process.env['NEXT_PUBLIC_IMGPROXY_KEY'] || '',
  salt: process.env['NEXT_PUBLIC_IMGPROXY_SALT'] || '',
  encode: true,
});

const bucket = process.env['NEXT_PUBLIC_IMGPROXY_BASKET'] || '';

export interface GenerateUrlProps {
  url: string;
  width?: number;
  height?: number;
  resizeType: ResizingType;
  dpr?: number;
  quality: number;
  format: string;
  isClient?: boolean;
}

const generateUrl = ({
  url,
  width,
  height,
  resizeType,
  dpr = 1,
  quality,
  format,
}: GenerateUrlProps): string => {
  if (!url) {
    return '';
  }

  if (url.startsWith('http')) {
    return url;
  }

  return imageProxyApi
    .builder()
    .resize(resizeType, width, height, false)
    .dpr(dpr)
    .gravity(GravityType.center)
    .format(format)
    .quality(quality)
    .generateUrl(`${bucket}${url}`);
};

const Image = memo(
  ({
    width,
    height,
    resizeType = 'fill',
    format = 'webp',
    src,
    alt = '',
    title = '',
    ...props
  }: ImageProps) => {
    const loader = configureLoader({
      width,
      height,
      resizeType,
      format,
      src,
    });

    return (
      <NextImage
        alt={alt}
        src={src}
        loader={loader}
        width={width}
        height={height}
        title={title || alt}
        {...props}
      />
    );
  },
);

export { Image };
